import { useHistory } from "react-router";
import { Header } from "src/pages/Welcome/components/Header";
import { Footer } from "src/pages/Welcome/components/Footer";
import { PrimaryButton } from "src/helpers/whiteLabel";
import { useAppContext } from "src/hooks/useAppContext";
import { useCallback, useEffect, useState } from "react";
import { UUID } from "@screencloud/uuid";
import { Button, Notification } from "@screencloud/screencloud-ui-components";
import {
  ChannelLayout,
  CreateDemoChannelMutationVariables,
  GetFilesByChannelIdDocument,
} from "src/types.g";
import { getColors, getLogoSrc, queryBrandInfo } from "src/helpers/brandHelper";
import { FormattedMessage } from "react-intl";
import apolloClient from "src/state/apolloClient";
import { updateNewChannelToList } from "src/helpers/channelHelper";

import {
  DesignMyScreenContainer,
  DesignMyScreenModalContainer,
  TopSection,
  MiddleSection,
} from "./styles";
import { StepProgress } from "src/components/StepProgress";
import { ApolloProps, withData } from "./apollo";
import { TemplateSelection } from "./TemplateSelection";
import { Styling } from "./Styling";
import { LayoutSelection } from "./LayoutSelection";
import { ChannelPreview } from "./ChannelPreview";
import { PIXI_TRIALIST_PREFERENCES_KEYS } from "../constants";
import { trackMixpanelEvent } from "src/utils/mixpanel";
import { CreateQuickPost } from "./CreateQuickPost";
import { TitleText } from "../components/TitleText";
import { useTemplateSelection } from "../hooks/useTemplateSelection";
import { useCreateQuickPost } from "../hooks/useCreateQuickPost";

export enum Step0ComponentEnum {
  TemplateSelection = "TemplateSelection",
  CreateQuickPost = "CreateQuickPost",
}

type DesignYourContentProps = ApolloProps & {
  step0: {
    component: Step0ComponentEnum;
    displayTitleText: boolean;
    stepName?: string;
  };
};

const DesignYourContentComponent = ({
  loading,
  commonAppInstanceTemplates,
  createDemoChannel,
  updateOrgPixiTrialistData,
  step0,
}: DesignYourContentProps) => {
  const history = useHistory();
  const context = useAppContext();
  const { stepName: step0Name = "Use case" } = step0 || {};
  const steps = [step0Name, "Styling", "Layout"];
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [presetColors, setPresetColors] = useState({
    primary: "#000000",
    secondary: "#ffffff",
  });
  const [logoFileId, setLogoFileId] = useState<UUID | null>(null);
  const [logoThumbnail, setLogoThumbnail] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<ChannelLayout | null>(
    null,
  );
  const [demoChannelId, setDemoChannelId] = useState<string>();
  const lastStep = steps.length - 1;
  const isLastStep = currentStepIndex === lastStep;
  const {
    selectedTemplateIds,
    handleTemplateClick,
    getCreateDemoChannelParams: getTemplateSelectionCreateDemoChannelParams,
    disableNextButton: templateSelectionDisableNextButton,
  } = useTemplateSelection();
  const {
    title: quickPostTitle,
    body: quickPostBody,
    appPlatformData: quickPostAppPlatformData,
    uploadedFiles: quickPostUploadedFiles,
    disableNextButton: quickPostDisableNextButton,
    setTitle: setQuickPostTitle,
    setBody: setQuickPostBody,
    setUploadedFiles: setQuickPostUploadedFiles,
    setAppPlatformData: setQuickPostAppPlatformData,
    getCreateDemoChannelParams: getQuickPostCreateDemoChannelParams,
  } = useCreateQuickPost();

  const isQuickPostPage =
    step0?.component === Step0ComponentEnum.CreateQuickPost;

  const step0DisableNextButton =
    templateSelectionDisableNextButton && quickPostDisableNextButton;

  useEffect(() => {
    trackMixpanelEvent({
      event: "Screen Design Initiated",
      context,
    });
  }, []);

  useEffect(() => {
    const fetchBrandInfo = async () => {
      const brandInfo = await queryBrandInfo(
        context.currentUser?.email?.split("@")[1] ?? "",
      );
      const { backgroundColor, textColor } = getColors(brandInfo);
      const logoSrc = getLogoSrc(brandInfo);
      setPresetColors({
        primary: backgroundColor ?? "#000000",
        secondary: textColor ?? "#ffffff",
      });
      setLogoThumbnail(logoSrc ?? null);
    };

    fetchBrandInfo();
  }, []);

  const handleGoBackToWelcome = useCallback(() => {
    history.push("/welcome");
  }, [history]);

  const checkFileStatus = async (channelId: string): Promise<boolean> => {
    const { data } = await apolloClient.query({
      query: GetFilesByChannelIdDocument,
      variables: { id: channelId },
      fetchPolicy: "network-only",
    });

    const files = data.channelById?.filesByChannelId?.nodes || [];
    return files.every((file) => file.fileProcessingStatus !== null);
  };

  const poll = async (
    checkFn: () => Promise<boolean>,
    interval = 3000,
    timeout = 60000,
  ): Promise<boolean> => {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const result = await checkFn();
      if (result) return true;
      await new Promise((resolve) => setTimeout(resolve, interval));
    }

    return false;
  };

  const handleNext = async () => {
    if (isLastStep && selectedLayout) {
      setIsGenerating(true);
      const createDemoChannelInput: CreateDemoChannelMutationVariables = {
        input: {
          spaceId: context.currentSpace?.id,
          commonAppInstanceTemplateIds:
            getTemplateSelectionCreateDemoChannelParams().refIdData, // TODO: Follow backend logic
          layout: selectedLayout,
          theme: {
            logoFileId,
            primaryColor: presetColors.primary,
            secondaryColor: presetColors.secondary,
          },
          content: isQuickPostPage
            ? getQuickPostCreateDemoChannelParams()
            : getTemplateSelectionCreateDemoChannelParams(),
        },
      };
      try {
        const { data } = await createDemoChannel({
          variables: createDemoChannelInput,
          update: (cache, { data }) => {
            if (data?.createDemoChannel?.channel) {
              updateNewChannelToList(
                context,
                cache,
                data?.createDemoChannel?.channel,
              );
            }
          },
        });
        if (data?.createDemoChannel?.channel?.id) {
          // Start polling for file status
          await poll(() =>
            checkFileStatus(data.createDemoChannel?.channel?.id),
          );

          setIsGenerating(false);
          setIsGenerated(true);
          setDemoChannelId(data.createDemoChannel?.channel?.id);
          updateOrgPixiTrialistData({
            variables: {
              input: {
                key: PIXI_TRIALIST_PREFERENCES_KEYS.COMPLETED_DESIGN_SCREEN_CONTENT,
                value: true,
              },
            },
          });
          return;
        }
      } catch (error) {
        setIsGenerating(false);
        console.error(error);
        Notification.error({
          message: context.intl.formatMessage({
            defaultMessage: "Something went wrong! Please try again.",
            id: "design_my_screen_modal.error",
          }),
        });
        return;
      } finally {
        trackMixpanelEvent({
          event: "Screen Design Completed",
          context,
        });
      }
    }
    if (currentStepIndex === 0) {
      trackMixpanelEvent({
        event: "Screen Design Use Case Completed",
        context,
      });
    }
    if (currentStepIndex === 1) {
      trackMixpanelEvent({
        event: "Screen Design Styling Completed",
        context,
      });
    }
    setCurrentStepIndex((prevIndex) => prevIndex + 1);
  };

  const handlePrevious = () => {
    if (currentStepIndex === 0) {
      return;
    }
    setCurrentStepIndex((prevIndex) => prevIndex - 1);
  };

  const onPrimaryColorChange = (color: string) => {
    setPresetColors((prevColors) => ({ ...prevColors, primary: color }));
  };

  const onSecondaryColorChange = (color: string) => {
    setPresetColors((prevColors) => ({ ...prevColors, secondary: color }));
  };

  const handleUploadLogo = async () => {
    setIsUploadingLogo(true);
    const files = await context.modal
      .openMediaUpload(
        <FormattedMessage
          id="design_my_screen_modal.styling.upload_logo"
          defaultMessage="Upload Logo"
        />,
        {
          maxFiles: 1,
          folderId: null,
          onClose: () => {
            setIsUploadingLogo(false);
          },
          isGlobalUpload: true,
        },
      )
      .catch((e) => {
        setIsUploadingLogo(false);
        return Promise.reject([]);
      });

    if (files?.length > 0) {
      setLogoFileId(files[0].id);
      const thumbnail = files[0].source;
      setLogoThumbnail(thumbnail);
    }
    context.modal.closeUploadModals();
    setIsUploadingLogo(false);
  };

  const handleLayoutClick = (layout: ChannelLayout) => {
    setSelectedLayout(layout);
  };

  const getContent = (): JSX.Element => {
    switch (currentStepIndex) {
      case 0:
        if (isQuickPostPage) {
          return (
            <CreateQuickPost
              title={quickPostTitle}
              body={quickPostBody}
              setTitle={setQuickPostTitle}
              setBody={setQuickPostBody}
              uploadedFiles={quickPostUploadedFiles}
              setUploadedFiles={setQuickPostUploadedFiles}
              appPlatformData={quickPostAppPlatformData}
              setAppPlatformData={setQuickPostAppPlatformData}
            />
          );
        } else {
          return (
            <TemplateSelection
              loading={loading}
              commonAppInstanceTemplates={commonAppInstanceTemplates}
              selectedTemplateIds={selectedTemplateIds}
              handleTemplateClick={handleTemplateClick}
            />
          );
        }
      case 1:
        return (
          <Styling
            presetColors={presetColors}
            onPrimaryColorChange={onPrimaryColorChange}
            onSecondaryColorChange={onSecondaryColorChange}
            handleUploadLogo={handleUploadLogo}
            logoThumbnail={logoThumbnail}
            isUploadingLogo={isUploadingLogo}
          />
        );
      case 2:
        return (
          <LayoutSelection
            selectedTemplateIds={selectedTemplateIds}
            commonAppInstanceTemplates={commonAppInstanceTemplates}
            selectedLayout={selectedLayout}
            handleLayoutClick={handleLayoutClick}
            logoThumbnail={logoThumbnail}
            presetColors={presetColors}
            isGenerating={isGenerating}
          />
        );
      default:
        return <div></div>;
    }
  };

  if (isGenerated && demoChannelId) {
    return (
      <DesignMyScreenContainer>
        <Header
          title="Nice, your content looks great!"
          onBackButtonClick={handleGoBackToWelcome}
        />
        <ChannelPreview channelId={demoChannelId} />
        <Footer justifyContent="center">
          <PrimaryButton onClick={handleGoBackToWelcome}>
            Complete
          </PrimaryButton>
        </Footer>
      </DesignMyScreenContainer>
    );
  }

  return (
    <DesignMyScreenContainer>
      <Header
        title="Design your content"
        onBackButtonClick={handleGoBackToWelcome}
      />

      <DesignMyScreenModalContainer>
        <TopSection>
          <StepProgress currentStep={currentStepIndex + 1} totalSteps={steps} />
          <div>
            {isGenerating ? (
              <TitleText
                currentStepIndex={currentStepIndex}
                titleText={context.intl.formatMessage({
                  id: "design_my_screen_modal.title.generating",
                  defaultMessage: "Generating your screen content...",
                })}
                subtitleText={context.intl.formatMessage({
                  id: "design_my_screen_modal.subtitle.generating",
                  defaultMessage:
                    "We are generating the perfect content for your digital signage. You can come back and edit this at anytime.",
                })}
              />
            ) : (
              <TitleText
                currentStepIndex={currentStepIndex}
                displayTitleText={step0?.displayTitleText ?? true}
              />
            )}
          </div>
        </TopSection>
        <MiddleSection>{getContent()}</MiddleSection>
      </DesignMyScreenModalContainer>
      <Footer
        justifyContent={currentStepIndex !== 0 ? "space-between" : "flex-end"}
      >
        {currentStepIndex > 0 && (
          <Button onClick={handlePrevious}>
            <FormattedMessage
              id="ui_component.common.label.back"
              defaultMessage="Back"
            />
          </Button>
        )}
        <PrimaryButton
          disabled={
            isUploadingLogo ||
            isGenerating ||
            (isLastStep && !selectedLayout) ||
            step0DisableNextButton
          }
          onClick={handleNext}
          loading={isGenerating}
        >
          {" "}
          {isLastStep ? (
            <FormattedMessage
              id="design_my_screen_modal.generate"
              defaultMessage="Generate"
            />
          ) : (
            <FormattedMessage
              id="design_my_screen_modal.next"
              defaultMessage="Next"
            />
          )}
        </PrimaryButton>
      </Footer>
    </DesignMyScreenContainer>
  );
};

export const DesignYourContent = withData(DesignYourContentComponent);
